import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import { LoginForm, ProFormText, ProFormCheckbox } from '@ant-design/pro-components';
import { Alert, Typography, Space, Modal, Button, Form, Input, Tooltip } from 'antd';
import { SecurityScanOutlined, SafetyCertificateOutlined, MailOutlined, EnvironmentOutlined } from '@ant-design/icons';
import CryptoJS from 'crypto-js';
import DOMPurify from 'dompurify';
import API from '../services/api';
import { GlobalStateContext } from '../components/GlobalStateProvider';
import { getLoginDeviceInfo } from '../utils/deviceInfo';
import ssoAuth from '../utils/ssoAuth';

const { Title, Paragraph, Text } = Typography;

const Login = () => {
  const [error, setError] = useState('');
  const [version, setVersion] = useState('');
  const [versionType, setVersionType] = useState('');
  const [updateLog, setUpdateLog] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [verificationModalVisible, setVerificationModalVisible] = useState(false);
  const [locationInfo, setLocationInfo] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verificationForm] = Form.useForm();
  const [isVerifying, setIsVerifying] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);
  const [resendCaptcha, setResendCaptcha] = useState({
    captchaId: '',
    captchaSvg: '',
    expiresIn: 0
  });
  const [ssoLoading, setSsoLoading] = useState(false);
  const formRef = useRef();
  const navigate = useNavigate();
  const { globalState, setGlobalState } = React.useContext(GlobalStateContext);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 重发验证码倒计时
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // 自动隐藏错误提示
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000); // 5秒后自动隐藏
      return () => clearTimeout(timer);
    }
  }, [error]);

  // 检查页面可见性，重置SSO加载状态
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && ssoLoading) {
        // 页面重新可见时，重置SSO加载状态
        setSsoLoading(false);
      }
    };

    const handlePageShow = () => {
      // 页面显示时（包括从历史记录返回）重置SSO加载状态
      setSsoLoading(false);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [ssoLoading]);

  // 获取重发用的图形验证码
  const getResendCaptcha = async () => {
    try {
      const response = await API.get('/api/register/captcha');
      setResendCaptcha({
        captchaId: response.data.captchaId,
        captchaSvg: response.data.captchaSvg,
        expiresIn: response.data.expiresIn
      });
    } catch (error) {
      console.error('获取验证码失败:', error);
      Modal.error({
        title: '获取验证码失败',
        content: '获取图形验证码失败，请稍后再试'
      });
    }
  };

  const encryptPassword = (password) => {
    const secretKey = 'your-secret-key';
    return CryptoJS.AES.encrypt(password, secretKey).toString();
  };

  const decryptPassword = (encryptedPassword) => {
    const secretKey = 'your-secret-key';
    const bytes = CryptoJS.AES.decrypt(encryptedPassword, secretKey);
    return bytes.toString(CryptoJS.enc.Utf8);
  };

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
    return '';
  };

  const setCookie = (name, value, days = 30) => {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + days);
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expirationDate.toUTCString()}; Secure; SameSite=Strict`;
  };

  const clearCookie = (name) => {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Strict`;
  };

  useEffect(() => {
    const username = getCookie('username');
    const password = getCookie('password');
    const rememberMe = getCookie('rememberMe') === 'true';

    if (username && password) {
      try {
        const decryptedPassword = decryptPassword(password);
        formRef.current?.setFieldsValue({
          username,
          password: decryptedPassword,
          rememberMe
        });
      } catch (error) {
        console.error('解密保存的密码时出错:', error);
        clearCookie('username');
        clearCookie('password');
        clearCookie('rememberMe');
      }
    }

    const fetchSystemInfo = async () => {
      try {
        const response = await API.get('/api/update/system-info');
        setVersion(response.data.version || 'v1.0.0');
        setVersionType(response.data.versionType || 'release');
        setUpdateLog(response.data.updateLog || '暂无更新日志');
      } catch (error) {
        console.error('获取系统信息失败:', error);
        // 设置默认值
        setVersion('v1.0.0');
        setVersionType('release');
        setUpdateLog('暂无更新日志');
      }
    };

    fetchSystemInfo();
  }, []);

  const handleSubmit = async (values) => {
    setError('');
    try {
      // 获取设备信息
      const deviceInfo = getLoginDeviceInfo();
      
      const loginData = {
        username: values.username,
        password: values.password,
        deviceId: deviceInfo.deviceId,
        deviceInfo: deviceInfo
      };

      const response = await API.post('/api/auth/login', loginData);

      // 处理需要异地登录验证的情况
      if (response.data.requireVerification) {
        setLocationInfo({
          currentLocation: response.data.currentLocation,
          lastLocation: response.data.lastLocation
        });
        setPendingLoginData(values); // 保存登录数据用于验证成功后处理
        setVerificationModalVisible(true);
        setResendCooldown(60); // 第一次发送验证码后立即开始倒计时
        
        // 获取重新发送验证码用的图形验证码
        getResendCaptcha();
        
        setError('');
        return;
      }

      // 处理正常登录成功
      await handleLoginSuccess(response.data, values);

    } catch (err) {
      console.error('登录失败:', err);
      if (err.response?.status === 403) {
        setError('账号已被封禁，请联系管理员');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('登录失败，请检查用户名和密码');
      }
    }
  };

  // 处理验证码提交
  const handleVerificationSubmit = async (verificationValues) => {
    setIsVerifying(true);
    try {
      // 获取设备信息
      const deviceInfo = getLoginDeviceInfo();
      
      const loginData = {
        username: pendingLoginData.username,
        password: pendingLoginData.password,
        verificationCode: verificationValues.verificationCode,
        deviceId: deviceInfo.deviceId,
        deviceInfo: deviceInfo
      };

      const response = await API.post('/api/auth/login', loginData);

      if (response.data.status === 'disabled') {
        setError('账号已被封禁，请联系管理员');
        return;
      }

      // 验证成功，关闭弹窗并完成登录
      setVerificationModalVisible(false);
      await handleLoginSuccess(response.data, pendingLoginData, true);

    } catch (err) {
      console.error('验证失败:', err);
      if (err.response?.data?.message) {
        Modal.error({
          title: '验证失败',
          content: err.response.data.message
        });
      } else {
        Modal.error({
          title: '验证失败',
          content: '验证失败，请重试'
        });
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // 统一处理登录成功逻辑
  const handleLoginSuccess = async (responseData, loginValues, isVerificationSuccess = false) => {
    const { token, role, locationChanged } = responseData;
    localStorage.setItem('token', token);
    localStorage.setItem('role', role);

    if (loginValues.rememberMe) {
      try {
        setCookie('username', loginValues.username);
        setCookie('password', encryptPassword(loginValues.password));
        setCookie('rememberMe', 'true');
      } catch (error) {
        console.error('保存登录信息时出错:', error);
      }
    } else {
      clearCookie('username');
      clearCookie('password');
      clearCookie('rememberMe');
    }

    // 更新全局状态
    setGlobalState({
      ...globalState,
      currentUser: {
        name: loginValues.username,
        role: role,
      },
    });

    // 如果是异地登录验证成功，显示提示信息
    if (isVerificationSuccess || locationChanged) {
      Modal.success({
        title: '异地登录验证成功',
        content: responseData.message || '您已成功通过异地登录验证',
        onOk: () => {
          navigate(role === 'admin' ? '/admin/dashboard' : '/user/dashboard');
        }
      });
    } else {
      navigate(role === 'admin' ? '/admin/dashboard' : '/user/dashboard');
    }
  };

  // 重新发送验证码
  const handleResendVerification = async (captchaCode) => {
    if (resendCooldown > 0 || !pendingLoginData?.username) return;

    if (!captchaCode) {
      Modal.error({
        title: '请输入图形验证码',
        content: '请先输入图形验证码再点击重新发送'
      });
      return;
    }

    setIsResending(true);
    try {
      const response = await API.post('/api/auth/resend-login-verification', {
        username: pendingLoginData.username,
        captchaId: resendCaptcha.captchaId,
        captchaCode: captchaCode
      });

      Modal.success({
        title: '验证码已重新发送',
        content: response.data.message || '验证码已发送到您的邮箱，请查收'
      });

      setResendCooldown(60); // 60秒冷却时间
      // 清除图形验证码字段并刷新验证码
      verificationForm.setFieldsValue({ resendCaptchaCode: '' });
      getResendCaptcha();
    } catch (err) {
      console.error('重发验证码失败:', err);
      let errorMessage = err.response?.data?.message || '重发验证码失败，请稍后再试';
      
      // 如果错误信息包含验证码相关内容，提供更明确的提示
      if (errorMessage.includes('验证码') || errorMessage.includes('captcha')) {
        errorMessage = '图形验证码错误，请重新输入';
      }
      
      Modal.error({
        title: '重发失败',
        content: errorMessage
      });
      
      // 刷新图形验证码并清除输入
      getResendCaptcha();
      verificationForm.setFieldsValue({ resendCaptchaCode: '' });
    } finally {
      setIsResending(false);
    }
  };

  // 关闭验证弹窗
  const handleCloseVerificationModal = () => {
    setVerificationModalVisible(false);
    setLocationInfo(null);
    setPendingLoginData(null);
    verificationForm.resetFields();
    setResendCooldown(0);
  };

  // SSO登录处理
  const handleSSOLogin = async () => {
    try {
      setSsoLoading(true);
      setError('');
      
      // 保存当前页面URL作为登录成功后的返回地址
      const currentPath = window.location.pathname;
      if (currentPath !== '/login') {
        sessionStorage.setItem('sso_return_url', currentPath);
      }
      
      await ssoAuth.startLogin(currentPath);
    } catch (error) {
      console.error('SSO登录启动失败:', error);
      setError('SSO登录启动失败: ' + error.message);
      setSsoLoading(false);
      
      // 显示更详细的错误信息
      Modal.error({
        title: 'SSO登录失败',
        content: error.message || '无法连接到SSO服务器，请稍后重试'
      });
    }
  };

  // 解析和格式化更新日志
  const parseUpdateLog = (logText) => {
    if (!logText || logText === '暂无更新日志') {
      return [{ version: 'v1.0.0', date: '', versionType: 'release', content: '暂无更新日志' }];
    }

    // 按行分割，过滤空行
    const lines = logText.split('\n').filter(line => line.trim());
    const parsedLogs = [];
    let currentEntry = null;

    lines.forEach(line => {
      const trimmedLine = line.trim();
      
      // 匹配版本行：日期 - 版本号 (版本类型) 或 日期 - 版本号
      const newFormatMatch = trimmedLine.match(/^(\d{8})\s*-\s*(.+?)\s*\(([^)]+)\)$/);
      const oldFormatMatch = trimmedLine.match(/^(\d{8})\s*-\s*(.+?)$/);
      
      if (newFormatMatch) {
        // 如果有当前条目，先保存
        if (currentEntry) {
          parsedLogs.push(currentEntry);
        }
        
        const dateStr = newFormatMatch[1];
        const version = newFormatMatch[2].trim();
        const versionType = newFormatMatch[3].trim().toLowerCase();
        
        // 格式化日期
        const formattedDate = `${dateStr.slice(0,4)}-${dateStr.slice(4,6)}-${dateStr.slice(6,8)}`;
        
        currentEntry = {
          version,
          date: formattedDate,
          versionType,
          content: []
        };
      } else if (oldFormatMatch && !newFormatMatch) {
        // 如果有当前条目，先保存
        if (currentEntry) {
          parsedLogs.push(currentEntry);
        }
        
        const dateStr = oldFormatMatch[1];
        const version = oldFormatMatch[2].trim();
        
        // 格式化日期
        const formattedDate = `${dateStr.slice(0,4)}-${dateStr.slice(4,6)}-${dateStr.slice(6,8)}`;
        
        currentEntry = {
          version,
          date: formattedDate,
          versionType: 'release',
          content: []
        };
      } else if (currentEntry && (trimmedLine.startsWith('-') || trimmedLine.startsWith('<'))) {
        // 这是更新内容行：以"-"开头的列表项或以"<"开头的HTML标签
        currentEntry.content.push(trimmedLine);
      } else if (currentEntry && trimmedLine) {
        // 如果有当前条目且不是空行，也作为内容处理（处理其他格式的内容）
        currentEntry.content.push(trimmedLine);
      } else if (trimmedLine && !currentEntry) {
        // 如果没有匹配到版本行且不是空行，可能是格式问题
        console.warn('无法解析的行:', trimmedLine);
      }
    });

    // 保存最后一个条目
    if (currentEntry) {
      parsedLogs.push(currentEntry);
    }

    // 处理content数组，转换为字符串
    const processedLogs = parsedLogs.map(log => ({
      ...log,
      content: log.content.length > 0 ? log.content.join('\n') : '无更新内容'
    }));

    // 去重：基于版本号和版本类型
    const uniqueLogs = [];
    const seen = new Set();
    
    processedLogs.forEach(log => {
      const key = `${log.version}-${log.versionType}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueLogs.push(log);
      }
    });

    // 限制只显示最近5个版本
    const recentLogs = uniqueLogs.length > 0 ? uniqueLogs.slice(0, 10) : [{ version: 'v1.0.0', date: '', versionType: 'release', content: '暂无更新日志' }];
    return recentLogs;
  };

  // 获取版本类型的显示配置
  const getVersionTypeConfig = (versionType) => {
    const configs = {
      alpha: { label: 'Alpha', color: '#ff4d4f', icon: '🔥', bgColor: '#fff2f0', description: '早期开发版本，功能不完整，可能存在较多问题' },
      beta: { label: 'Beta', color: '#fa8c16', icon: '⚡', bgColor: '#fff7e6', description: '功能基本完成，正在进行测试的版本' },
      rc: { label: 'RC', color: '#1890ff', icon: '🚀', bgColor: '#e6f7ff', description: '即将正式发布的候选版本，功能完整' },
      sp: { label: 'SP', color: '#722ed1', icon: '🔧', bgColor: '#f9f0ff', description: '基于正式版的补丁包，包含bug修复和小幅改进' },
      release: { label: 'Release', color: '#52c41a', icon: '✅', bgColor: '#f6ffed', description: '经过充分测试的正式发布版本' }
    };
    return configs[versionType?.toLowerCase()] || configs.release;
  };

  // 检测内容是否包含HTML标签
  const containsHTML = (content) => {
    return /<[a-z][\s\S]*>/i.test(content);
  };

  // 渲染更新日志内容（支持HTML）
  const renderLogContent = (content) => {
    if (containsHTML(content)) {
      // 在HTML处理前，先将换行符转换为<br>标签
      const contentWithLineBreaks = content.replace(/\n/g, '<br>');
      
      // 配置DOMPurify以允许常用的安全标签和属性
      const cleanHTML = DOMPurify.sanitize(contentWithLineBreaks, {
        ALLOWED_TAGS: ['p', 'div', 'span', 'br', 'strong', 'b', 'em', 'i', 'u', 'small', 'mark', 'del', 'ins', 'sub', 'sup'],
        ALLOWED_ATTR: ['style', 'class'],
        ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i
      });
      
      return (
        <div 
          style={{
            fontSize: '13px',
            lineHeight: '1.5',
            color: '#595959',
            fontFamily: 'inherit'
          }}
          dangerouslySetInnerHTML={{ __html: cleanHTML }}
        />
      );
    } else {
      return (
        <div style={{
          fontSize: '13px',
          lineHeight: '1.5',
          color: '#595959',
          whiteSpace: 'pre-wrap',
          fontFamily: 'inherit'
        }}>
          {content}
        </div>
      );
    }
  };

  return (
    <div style={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: '#f0f2f5',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden' // 防止整体页面滚动
    }}>
      {/* 顶部错误提示 */}
      {error && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#ff4d4f',
          color: 'white',
          padding: isMobile ? '10px 16px' : '12px 20px',
          borderRadius: '8px',
          fontSize: isMobile ? '13px' : '14px',
          fontWeight: 500,
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '80vw',
          width: 'fit-content',
          animation: 'slideInDown 0.3s ease-out',
          whiteSpace: isMobile ? 'normal' : 'nowrap'
        }}>
          <span style={{
            fontSize: isMobile ? '14px' : '16px',
            marginRight: '4px',
            flexShrink: 0
          }}>
            {error.includes('封禁') ? '🚫' : '⚠️'}
          </span>
          <span style={{ flex: 1 }}>{error}</span>
          <button
            onClick={() => setError('')}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: isMobile ? '16px' : '18px',
              padding: '0',
              lineHeight: 1,
              opacity: 0.8,
              transition: 'opacity 0.2s ease',
              flexShrink: 0,
              marginLeft: '8px'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.8'}
            title="关闭"
          >
            ×
          </button>
        </div>
      )}
      
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '16px' : '24px',
        paddingTop: isMobile ? '16px' : '24px', // 移除动态paddingTop，因为现在是居中小提示框
        overflow: 'hidden',
        minHeight: 0 // 确保flex子元素可以收缩
      }}>
        <div style={{ 
          display: 'flex', 
          width: '100%', 
          maxWidth: '1200px', 
          background: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          overflow: 'hidden',
          height: isMobile ? 'fit-content' : '600px',
          maxHeight: isMobile ? 'calc(100vh - 80px)' : '600px', // 移动端增加更多缓冲空间
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          {!isMobile && (
            <div style={{ 
              flex: 1, 
              padding: '48px', 
              background: '#fafafa',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              overflow: 'hidden'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'auto',
                paddingRight: '16px'
              }}>
                <img
                  src="https://tc-oss-2.1yidc.com/20250830/eeabe6e8038b78ab2fd25f5043a29405.png"
                  alt="Logo"
                  style={{ 
                    width: '200px', 
                    marginBottom: '48px',
                    alignSelf: 'center'
                  }}
                />
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Title level={4}>
                      <SecurityScanOutlined /> DDoS防护系统
                    </Title>
                    <Paragraph>
                      我们的系统提供实时监控和管理DDoS流量的功能，帮助您确保服务器的稳定运行和安全性。
                    </Paragraph>
                  </div>
                  <div>
                    <Title level={4}>
                      <SafetyCertificateOutlined /> 智能流量分析
                    </Title>
                    <Paragraph>
                      通过先进的流量分析技术，快速识别异常流量，并提供实时警报和防护建议。
                    </Paragraph>
                  </div>
                  <div>
                    <Title level={4}>
                      <SecurityScanOutlined /> 全方位防护
                    </Title>
                    <Paragraph>
                      提供多层次的DDoS防护策略，包括UDP防护、流量限制等，让您的服务更加安全。
                    </Paragraph>
                  </div>
                </Space>
              </div>
            </div>
          )}

          <div style={{ 
            flex: 1, 
            padding: isMobile ? '16px' : '48px', // 移动端进一步减少padding
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            overflow: 'hidden',
            minHeight: 0 // 确保flex子元素可以收缩
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: isMobile ? 'auto' : 'auto', // 移动端允许滚动
              paddingRight: isMobile ? '0' : '16px',
              maxHeight: isMobile ? 'calc(100vh - 96px)' : 'none' // 移动端进一步限制内容高度，预留更多空间给Alert
            }}>
              {isMobile && (
                <img
                  src="https://tc-oss-2.1yidc.com/20250830/eeabe6e8038b78ab2fd25f5043a29405.png"
                  alt="Logo"
                  style={{ 
                    width: '120px', // 移动端进一步缩小logo
                    marginBottom: '16px', // 进一步减少底部间距
                    alignSelf: 'center'
                  }}
                />
              )}

              {/* 正常登录界面 */}
              <LoginForm
                formRef={formRef}
                title="盛夏云 安全平台"
                subTitle="专业的DDoS流量监控系统"
                onFinish={handleSubmit}
                style={{ width: '100%' }}
              >
                <ProFormText
                  name="username"
                  fieldProps={{
                    size: isMobile ? 'middle' : 'large', // 移动端使用中等尺寸
                    prefix: <SecurityScanOutlined />
                  }}
                  placeholder="用户名"
                  rules={[{ required: true, message: '请输入用户名' }]}
                />
                
                <ProFormText.Password
                  name="password"
                  fieldProps={{
                    size: isMobile ? 'middle' : 'large', // 移动端使用中等尺寸
                    prefix: <SafetyCertificateOutlined />
                  }}
                  placeholder="密码"
                  rules={[{ required: true, message: '请输入密码' }]}
                />
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: isMobile ? 12 : 24, // 移动端进一步减少间距
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '8px' : '0' // 进一步减少移动端间距
                }}>
                  <ProFormCheckbox 
                    name="rememberMe"
                    fieldProps={{
                      onChange: (e) => {
                        if (!e.target.checked) {
                          clearCookie('username');
                          clearCookie('password');
                          clearCookie('rememberMe');
                        }
                      }
                    }}
                  >
                    记住用户名和密码
                  </ProFormCheckbox>
                  <div style={{
                    display: 'flex',
                    gap: isMobile ? '12px' : '16px', // 移动端减少间距
                    justifyContent: isMobile ? 'center' : 'flex-end',
                    width: isMobile ? '100%' : 'auto'
                  }}>
                    <a href="/register">注册账号</a>
                    <a href="/forgot-password">忘记密码？</a>
                  </div>
                </div>

                {/* 其他登录方式 */}
                <div style={{
                  textAlign: 'center',
                  marginBottom: isMobile ? 8 : 16, // 移动端进一步减少间距
                  padding: isMobile ? '8px' : '12px', // 移动端进一步减少padding
                  backgroundColor: '#f6f8fa',
                  borderRadius: '6px',
                  border: '1px solid #e1e4e8',
                  display: 'flex',
                  gap: isMobile ? '8px' : '16px',
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  {/* RAM登录按钮 */}
                  <Button 
                    type="link" 
                    size="small"
                    onClick={() => navigate('/ram-login')}
                    style={{ 
                      padding: '0 8px',
                      fontSize: isMobile ? '13px' : '14px', // 移动端缩小字体
                      fontWeight: 500,
                      flex: isMobile ? 'none' : '1'
                    }}
                  >
                    <SecurityScanOutlined style={{ marginRight: '4px' }} />
                    RAM账号登录
                  </Button>

                  {/* 分隔符 */}
                  {!isMobile && (
                    <div style={{
                      width: '1px',
                      height: '20px',
                      backgroundColor: '#d9d9d9'
                    }}></div>
                  )}

                  {/* SSO登录按钮 */}
                  <Button 
                    type="link" 
                    size="small"
                    loading={ssoLoading}
                    onClick={handleSSOLogin}
                    style={{ 
                      padding: '0 8px',
                      fontSize: isMobile ? '13px' : '14px', // 移动端缩小字体
                      fontWeight: 500,
                      flex: isMobile ? 'none' : '1'
                    }}
                  >
                    <SafetyCertificateOutlined style={{ marginRight: '4px' }} />
                    {ssoLoading ? '正在跳转...' : '统一认证登录'}
                  </Button>
                </div>
              </LoginForm>
            </div>
          </div>
        </div>
      </div>

      {/* 异地登录验证弹窗 */}
      <Modal
        title={
          <div style={{ 
            textAlign: 'center',
            padding: isMobile ? '4px 0' : '8px 0' // 移动端减少padding
          }}>
            <EnvironmentOutlined style={{ 
              fontSize: isMobile ? '18px' : '20px', // 移动端稍微缩小图标
              color: '#faad14', 
              marginRight: '8px'
            }} />
            <span style={{ 
              fontSize: isMobile ? '14px' : '16px', // 移动端缩小字体
              fontWeight: 500
            }}>
              异地登录验证
            </span>
          </div>
        }
        open={verificationModalVisible}
        onCancel={handleCloseVerificationModal}
        footer={null}
        width={isMobile ? '95%' : 480} // 移动端增加宽度利用率
        destroyOnClose
        maskClosable={false}
        centered
      >
        <div style={{ padding: isMobile ? '16px 0' : '20px 0' }}> {/* 移动端减少padding */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: isMobile ? '16px' : '24px', // 移动端减少间距
            padding: isMobile ? '12px' : '16px', // 移动端减少padding
            background: '#f6ffed',
            borderRadius: '6px',
            border: '1px solid #b7eb8f'
          }}>
            <div style={{ 
              fontSize: isMobile ? '20px' : '24px', // 移动端缩小emoji
              marginBottom: isMobile ? '6px' : '8px' // 移动端减少间距
            }}>
              🔒
            </div>
            <Paragraph style={{ 
              margin: 0,
              color: '#52c41a',
              fontSize: isMobile ? '12px' : '14px' // 移动端缩小字体
            }}>
              检测到您在新的地点登录，为了保障账户安全，请验证您的身份。验证码已发送到您的邮箱，请查收。
            </Paragraph>
          </div>

          {locationInfo && (
            <Alert
              message="位置信息"
              description={
                <div>
                  <div style={{ marginBottom: '8px' }}>
                    <Text strong style={{ fontSize: isMobile ? '12px' : '14px' }}>当前登录位置：</Text>
                    <Text style={{ fontSize: isMobile ? '12px' : '14px' }}>{locationInfo.currentLocation}</Text>
                  </div>
                  <div>
                    <Text strong style={{ fontSize: isMobile ? '12px' : '14px' }}>上次登录位置：</Text>
                    <Text style={{ fontSize: isMobile ? '12px' : '14px' }}>{locationInfo.lastLocation}</Text>
                  </div>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: isMobile ? '16px' : '24px' }} // 移动端减少间距
            />
          )}

          <Form
            form={verificationForm}
            onFinish={handleVerificationSubmit}
            layout="vertical"
          >
            <Form.Item
              name="verificationCode"
              label={<span style={{ fontSize: isMobile ? '13px' : '14px' }}>邮箱验证码</span>}
              rules={[{ required: true, message: '请输入验证码' }]}
              style={{ marginBottom: isMobile ? '16px' : '24px' }} // 移动端减少间距
            >
              <Input
                size={isMobile ? 'middle' : 'large'} // 移动端使用中等尺寸
                prefix={<MailOutlined />}
                placeholder="请输入6位数字验证码"
                style={{ borderRadius: '6px' }}
              />
            </Form.Item>

            {/* 重新发送验证码区域 */}
            <div style={{
              background: '#fafafa',
              borderRadius: '6px',
              padding: isMobile ? '12px' : '16px',
              marginBottom: isMobile ? '16px' : '24px',
              border: '1px solid #f0f0f0'
            }}>
              <div style={{ marginBottom: '12px' }}>
                <Text style={{ fontSize: isMobile ? '12px' : '13px', color: '#666' }}>
                  没有收到验证码？请先输入图形验证码，然后点击重新发送
                </Text>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <div 
                  style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    minWidth: '80px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fff'
                  }}
                  dangerouslySetInnerHTML={{ __html: resendCaptcha.captchaSvg }}
                  onClick={getResendCaptcha}
                  title="点击刷新验证码"
                />
                <Form.Item 
                  name="resendCaptchaCode" 
                  style={{ margin: 0, flex: 1 }}
                  rules={[{ required: false }]}
                >
                  <Input 
                    placeholder="图形验证码"
                    maxLength={4}
                    style={{ textTransform: 'uppercase' }}
                    size={isMobile ? 'middle' : 'large'}
                  />
                </Form.Item>
                <Button 
                  icon={<SecurityScanOutlined />} 
                  onClick={getResendCaptcha}
                  size={isMobile ? 'middle' : 'large'}
                  title="刷新验证码"
                />
              </div>
              
              <Button 
                type="dashed"
                onClick={() => handleResendVerification(verificationForm.getFieldValue('resendCaptchaCode'))}
                loading={isResending}
                disabled={resendCooldown > 0}
                block
                size={isMobile ? 'middle' : 'large'}
              >
                {resendCooldown > 0 ? `${resendCooldown}秒后可重新发送` : '重新发送验证码'}
              </Button>
            </div>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isVerifying}
                size={isMobile ? 'middle' : 'large'} // 移动端使用中等尺寸
                block
                style={{
                  borderRadius: '6px',
                  height: isMobile ? '36px' : '40px' // 移动端减少高度
                }}
              >
                {isVerifying ? '验证中...' : '验证并登录'}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Modal>

      <div style={{ 
        textAlign: 'center', 
        padding: isMobile ? '6px' : '16px', // 移动端进一步减少padding
        background: '#fff',
        borderTop: '1px solid #f0f0f0',
        fontSize: isMobile ? '10px' : '14px', // 移动端进一步缩小字体
        flexShrink: 0, // 防止被压缩
        minHeight: isMobile ? '28px' : 'auto' // 移动端减少最小高度
      }}>
        <Text style={{ fontSize: isMobile ? '10px' : '14px' }}>
          本系统由 <a href="https://www.sxdidc.com" target="_blank" rel="noopener noreferrer">盛夏云</a> 提供技术支持 
          <Text style={{ margin: isMobile ? '0 2px' : '0 8px' }}>|</Text>
          <Text>系统版本：</Text>
          <Text 
            style={{ 
              color: '#1890ff', 
              cursor: 'pointer',
              marginLeft: 4,
              fontSize: isMobile ? '10px' : '14px'
            }} 
            onClick={() => setIsModalVisible(true)}
          >
            {version}
          </Text>
        </Text>
      </div>

      <Modal
        title={
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            <span>📋</span>
            <span>系统更新日志</span>
            <span style={{
              background: '#e6f7ff',
              color: '#1890ff',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '11px',
              border: '1px solid #91d5ff'
            }}>
              显示最近10个版本
            </span>
            <span style={{
              background: '#f0f0f0',
              color: '#666',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '12px'
            }}>
              当前版本 {version}
              {versionType && (() => {
                const typeConfig = getVersionTypeConfig(versionType);
                return (
                  <Tooltip title={typeConfig.description} placement="bottom">
                    <span style={{
                      background: typeConfig.bgColor,
                      color: typeConfig.color,
                      padding: '1px 4px',
                      borderRadius: '8px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      border: `1px solid ${typeConfig.color}`,
                      marginLeft: '4px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '1px',
                      cursor: 'help'
                    }}>
                      <span style={{ fontSize: '8px' }}>{typeConfig.icon}</span>
                      {typeConfig.label}
                    </span>
                  </Tooltip>
                );
              })()}
            </span>
          </div>
        }
        open={isModalVisible}
        onOk={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setIsModalVisible(false)}>
            确定
          </Button>
        ]}
        width={isMobile ? '90%' : 700}
        styles={{ 
          body: { 
            maxHeight: '70vh', 
            overflow: 'auto',
            padding: '20px',
            background: '#ffffff'
          } 
        }}
      >
        <div style={{
          fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
          fontSize: '14px',
          lineHeight: '1.6',
          color: '#333',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          {!updateLog ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#8c8c8c'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
              <div>正在加载更新日志...</div>
            </div>
          ) : parseUpdateLog(updateLog).length === 1 && parseUpdateLog(updateLog)[0].content === '暂无更新日志' ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#8c8c8c'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
              <div>暂无更新日志</div>
              <div style={{ fontSize: '12px', marginTop: '8px' }}>
                请联系管理员获取最新版本信息
              </div>
            </div>
          ) : (
            parseUpdateLog(updateLog).map((log, index) => {
              // 判断是否为当前版本：同时比较版本号和版本类型
              const versionMatches = log.version === version || 
                (version.startsWith('v') ? log.version === version : log.version === `v${version}`) ||
                (log.version.startsWith('v') ? log.version.slice(1) === version : `v${log.version}` === version);
              
              const typeMatches = (log.versionType || 'release') === (versionType || 'release');
              
              const isCurrentVersion = versionMatches && typeMatches;

              return (
                <div 
                  key={index}
                  style={{
                    background: isCurrentVersion ? '#f6ffed' : '#fafafa',
                    border: `1px solid ${isCurrentVersion ? '#b7eb8f' : '#d9d9d9'}`,
                    borderRadius: '6px',
                    padding: '16px',
                    marginBottom: index < parseUpdateLog(updateLog).length - 1 ? '12px' : '0',
                    position: 'relative'
                  }}
                >
                  {/* 版本标识 */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '8px',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    <span style={{
                      background: isCurrentVersion ? '#52c41a' : '#8c8c8c',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {log.version}
                    </span>
                    
                    {/* 版本类型徽章 */}
                    {log.versionType && (() => {
                      const typeConfig = getVersionTypeConfig(log.versionType);
                      return (
                        <Tooltip title={typeConfig.description} placement="top">
                          <span style={{
                            background: typeConfig.bgColor,
                            color: typeConfig.color,
                            padding: '2px 6px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            border: `1px solid ${typeConfig.color}`,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '2px',
                            cursor: 'help'
                          }}>
                            <span>{typeConfig.icon}</span>
                            {typeConfig.label}
                          </span>
                        </Tooltip>
                      );
                    })()}
                    
                    {log.date && (
                      <span style={{
                        color: '#8c8c8c',
                        fontSize: '12px'
                      }}>
                        📅 {log.date}
                      </span>
                    )}
                    {isCurrentVersion && (
                      <span style={{
                        background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        ⭐ 当前版本
                      </span>
                    )}
                  </div>
                  
                  {/* 更新内容 */}
                  {renderLogContent(log.content)}
                </div>
              );
            })
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Login;